import { useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { SignOutButton } from "@/core/auth/components/SignOutButton";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { isAuthenticationError } from "@/core/auth/utils/authErrors";
import { useBusinessLocations } from "@/features/business/hooks/businessLocationQueries";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import type { Business } from "@/features/business/types/businessTypes";
import type { BusinessLocation } from "@/features/business/types/businessLocationTypes";
import { useCurrentProfile } from "@/features/profile/hooks/useCurrentProfile";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { SetupHeader } from "@/shared/components/ui/SetupHeader";
import { BusinessOnboardingPage } from "../pages/BusinessOnboardingPage";
import { LocationOnboardingPage } from "../pages/LocationOnboardingPage";
import { OnboardingSummaryPage } from "../pages/OnboardingSummaryPage";
import { ProfileOnboardingPage } from "../pages/ProfileOnboardingPage";
import { WelcomePage } from "../pages/WelcomePage";

interface OnboardingGuardProps {
  children: ReactNode;
}

function SetupStatus({ error, onRetry }: { error?: Error | null; onRetry?: () => void }) {
  const { signOut } = useAuth();
  if (!error) return <LoadingSpinner fullScreen />;

  return (
    <div className="h-dvh">
      <Page>
        <SetupHeader title="Your account" />
        <PageContent>
          <div className="mx-auto w-full max-w-md">
            <ErrorState message={error.message} onRetry={onRetry}>
              {isAuthenticationError(error) && <SignOutButton signOut={signOut} />}
            </ErrorState>
          </div>
        </PageContent>
      </Page>
    </div>
  );
}

function AccountOnboardingGuard({ children }: OnboardingGuardProps) {
  const route = useLocation();
  const { businesses, business: selectedBusiness, location: selectedLocation, loading } = useCurrentBusiness();
  const { profile, isLoading: profileLoading, error, refetch } = useCurrentProfile();
  const [started, setStarted] = useState(() => new URLSearchParams(route.search).get("setup") === "start");
  const [completed, setCompleted] = useState(false);
  const [editing, setEditing] = useState<"profile" | "business" | "location" | null>(null);
  const [savedBusiness, setSavedBusiness] = useState<Business>();
  const [savedLocation, setSavedLocation] = useState<BusinessLocation>();
  const business = (!completed ? savedBusiness : undefined) ?? selectedBusiness ?? businesses[0];
  const locationsQuery = useBusinessLocations(profile ? business?.id ?? "" : "");
  const location = (!completed ? savedLocation : undefined)
    ?? (selectedLocation?.businessId === business?.id ? selectedLocation : undefined)
    ?? locationsQuery.data?.find((entry) => entry.businessId === business?.id);

  if (profileLoading || error || loading) {
    return <SetupStatus error={error} onRetry={() => void refetch()} />;
  }

  if (profile && business && !location && (locationsQuery.isLoading || locationsQuery.error)) {
    return <SetupStatus error={locationsQuery.error} onRetry={() => void locationsQuery.refetch()} />;
  }

  const needsSetup = !profile || !business || !location;
  if (needsSetup && !started) {
    return <WelcomePage onStart={() => setStarted(true)} />;
  }

  if (!profile || editing === "profile") {
    return <ProfileOnboardingPage profile={profile ?? undefined} onSaved={() => setEditing(null)} />;
  }

  if (!business || editing === "business") {
    return <BusinessOnboardingPage business={business} onSaved={(saved) => {
      setSavedBusiness(saved);
      setEditing(null);
    }} />;
  }

  if (!location || editing === "location") {
    return <LocationOnboardingPage businessId={business.id} location={location} onSaved={(saved) => {
      setSavedLocation(saved);
      setEditing(null);
    }} />;
  }

  if (started && !completed) {
    return <OnboardingSummaryPage
      profile={profile}
      business={business}
      location={location}
      onComplete={() => setCompleted(true)}
      onEditProfile={() => setEditing("profile")}
      onEditBusiness={() => setEditing("business")}
      onEditLocation={() => setEditing("location")}
    />;
  }

  return <>{children}</>;
}

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <SetupStatus />;
  if (!user) {
    return <Navigate to="/login" state={{ from: `${location.pathname}${location.search}${location.hash}` }} replace />;
  }

  return <AccountOnboardingGuard key={user.id}>{children}</AccountOnboardingGuard>;
}
